import { ReactElement, ReactNode } from 'react';
import { Helmet, HelmetProps } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { commonConfig, metaData as md } from '../../config';
import { defaultSecurityPolicy } from '../../config/csp';

export interface MetaProps extends HelmetProps {
  title?: string;
  description?: string;
  url?: string;
  image?: {
    url: string;
    size: [number | string, number | string];
  };
  type?: string;
  social?: boolean;
  noTrack?: boolean;
  branch?: boolean;
  contentSecurityPolicy?: string;
  children?: ReactNode;
}
const Meta = ({
  title,
  description,
  url,
  image,
  type = 'video',
  social = true,
  noTrack,
  branch,
  contentSecurityPolicy = defaultSecurityPolicy,
  children,
  ...rest
}: MetaProps): ReactElement | null => {
  const { t } = useTranslation();
  const companyName = commonConfig.company.name;
  const metaData = {
    title: title ? `${title} | ${companyName}` : t('global.metaTitle'),
    description: description || t('global.metaDescription'),
    image: image || {
      url: `${md.imageURL}`,
      size: [512, 512],
    },
    url: url || window.location.href,
  };

  const includeSocial = !noTrack && social;
  return (
    <Helmet {...rest}>
      <title>{metaData.title}</title>
      <meta name="description" content={metaData.description} />
      <meta name="author" content={companyName} />
      <meta httpEquiv="Content-Security-Policy" content={contentSecurityPolicy} />
      {includeSocial
        ? [
            <meta key="name" itemProp="name" content={metaData.title} />,
            <meta key="description" itemProp="description" content={metaData.description} />,
            <meta key="image" itemProp="image" content={metaData.image.url} />,
            <meta key="og:title" property="og:title" content={metaData.title} />,
            <meta key="og:type" property="og:type" content={type} />,
            <meta key="og:url" property="og:url" content={metaData.url} />,
            <meta key="og:image" property="og:image" content={metaData.image.url} />,
            <meta
              key="og:image:width"
              property="og:image:width"
              content={String(metaData.image.size[0])}
            />,
            <meta
              key="og:image:height"
              property="og:image:height"
              content={String(metaData.image.size[1])}
            />,
            <meta key="og:description" property="og:description" content={metaData.description} />,
            <meta key="og:site_name" property="og:site_name" content={companyName} />,
            <meta key="fb:app_id" property="fb:app_id" content={commonConfig.facebook.appId} />,
            <meta key="twitter:card" name="twitter:card" content="summary_large_image" />,
            <meta key="twitter:site" name="twitter:site" content={companyName} />,
            <meta key="twitter:title" name="twitter:title" content={metaData.title} />,
            <meta
              key="twitter:description"
              name="twitter:description"
              content={metaData.description}
            />,
            <meta key="twitter:domain" name="twitter:domain" content={metaData.url} />,
            <meta key="twitter:image" name="twitter:image" content={metaData.image.url} />,
          ]
        : null}
      {branch ? (
        <>
          <meta name="branch:deeplink:$android_url" content={url} />
          <meta name="branch:deeplink:$ios_url" content={url} />
          <meta name="branch:deeplink:$desktop_url" content={url} />
        </>
      ) : null}
      {noTrack ? <meta name="robots" content="noindex, nofollow" /> : null}
      {children}
    </Helmet>
  );
};

export default Meta;
